import React, { useEffect, useState } from 'react';
import { Time } from '@carbon/icons-react';
import {
  formatShortDate,
  formatTime,
  getCountdown,
  getRelativeDayLabel,
  getShiftPhase,
  getTimeRemaining,
} from './workDayUtils';
import ShiftNotesPanel from './ShiftNotesPanel';

/**
 * OnShiftBar - traka sa trenutnom smenom: status, oblast, vreme smene i odbrojavanje.
 * Stanje se osvežava svake sekunde.
 *
 * Ako smena još nije počela, traka prikazuje "Smena uskoro" i odbrojavanje do
 * početka (a ne "U smeni").
 *
 * Ako je smena već završena, u istoj traci se umesto nje prikazuje naredna smena
 * (dan, vreme i oblast), a ako naredne smene nema - ostaje poruka "Smena je završena".
 */
const OnShiftBar = ({ todayShift, nextShift = null, loading = false, onOpenResident = null }) => {
  // "Sada" pomeramo svake sekunde kako bi se faza smene (predstoji / u toku / završena)
  // i odbrojavanje sami osvežavali bez ponovnog učitavanja podataka.
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    const interval = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(interval);
  }, []);

  if (loading) {
    return <span className="wdash-onshift__skeleton" aria-busy="true" />;
  }

  if (!todayShift) {
    return (
      <div className="wdash-onshift wdash-onshift--muted">
        <div className="wdash-onshift__main">
          <div className="wdash-onshift__block">
            <span className="wdash-onshift__label">
              <span className="wdash-onshift__dot" style={{ background: 'var(--cds-text-secondary, #8d8d8d)' }} />
              Status
            </span>
            <span className="wdash-onshift__area">Niste u smeni danas</span>
          </div>
        </div>
      </div>
    );
  }

  const phase = getShiftPhase(todayShift, now);
  // Smena još nije počela (startTime je u budućnosti) -> ne prikazujemo "U smeni"
  const isUpcoming = phase === 'upcoming';
  const isEnded = phase === 'ended';

  const hasNextShift =
    Boolean(nextShift) &&
    Boolean(nextShift.startTime || nextShift.endTime || nextShift.area);
  // Smena je završena -> na mestu trake "U smeni" prikazujemo narednu smenu
  const showNext = isEnded && hasNextShift;

  // Odbrojavanje do početka (dok smena predstoji) i do kraja (dok traje)
  const countdownToStart = getCountdown(todayShift.startTime, now);
  const remaining = getTimeRemaining(todayShift.endTime, now);

  return (
    <div
      className={`wdash-onshift ${
        showNext ? 'wdash-onshift--next' : isUpcoming ? 'wdash-onshift--upcoming' : ''
      }`}
    >
      <div className="wdash-onshift__main">
        <div className="wdash-onshift__block">
          <span className="wdash-onshift__label">
            <span className="wdash-onshift__dot" />
            {showNext ? 'Naredna smena' : isUpcoming ? 'Smena uskoro' : 'U smeni'}
          </span>
          <span className="wdash-onshift__area">
            {showNext ? nextShift.area || 'Naredna smena' : todayShift.area || '-'}
          </span>
        </div>

        <span className="wdash-onshift__divider wdash-onshift__divider--inner" aria-hidden="true" />

        <span className="wdash-onshift__time">
          <Time size={16} />
          {showNext
            ? `${formatTime(nextShift.startTime)}–${formatTime(nextShift.endTime)}`
            : `${formatTime(todayShift.startTime)}–${formatTime(todayShift.endTime)}`}
        </span>
      </div>

      {showNext ? (
        <>
          <span className="wdash-onshift__divider wdash-onshift__divider--outer" aria-hidden="true" />
          <div className="wdash-onshift__right">
            <span className="wdash-onshift__rem-label">
              {getRelativeDayLabel(nextShift.startTime)}
            </span>
            <span className="wdash-onshift__rem-value">
              {formatShortDate(nextShift.startTime)}
            </span>
          </div>
        </>
      ) : isUpcoming ? (
        <>
          <span className="wdash-onshift__divider wdash-onshift__divider--outer" aria-hidden="true" />
          <div className="wdash-onshift__right">
            <span className="wdash-onshift__rem-label">
              <span className="wdash-only-desktop">Do početka smene</span>
              <span className="wdash-only-mobile">Počinje za</span>
            </span>
            <span className="wdash-onshift__rem-value">
              {countdownToStart
                ? `${countdownToStart.hours} h ${countdownToStart.minutes} min`
                : formatTime(todayShift.startTime)}
            </span>
          </div>
        </>
      ) : remaining ? (
        <>
          <span className="wdash-onshift__divider wdash-onshift__divider--outer" aria-hidden="true" />
          <div className="wdash-onshift__right">
            {isEnded ? (
              <span className="wdash-onshift__rem-value">Smena je završena</span>
            ) : (
              <>
                <span className="wdash-onshift__rem-label">
                  <span className="wdash-only-desktop">Do kraja smene</span>
                  <span className="wdash-only-mobile">Još</span>
                </span>
                <span className="wdash-onshift__rem-value">
                  {remaining.hours} h {remaining.minutes} min
                </span>
              </>
            )}
          </div>
        </>
      ) : null}

      {/* Beleške trenutne smene - unutar iste trake "U smeni" (nove linije u traci).
          Dok smena ne počne (ili je prikazana naredna smena) beleške se ne prikazuju. */}
      {!showNext && !isUpcoming && (
        <ShiftNotesPanel shift={todayShift} onOpenResident={onOpenResident} />
      )}
    </div>
  );
};

export default OnShiftBar;
